import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Badge, Button, Skeleton } from '@roger/ui-components';
import { X } from 'lucide-react';
import type { TaskListResponse } from '../services/api/types.gen';
import { getClinicianDisplayName } from '../utils/clinicianUtils';
import { useMediaQuery } from '../hooks/useIsMobile';
import { useCalendarVisitPopout } from '../hooks/useCalendarVisitPopout';
import type { CalendarCreateVisitClinician } from './CalendarCreateVisitDialog';
import LupaThresholdGauge from './LupaThresholdGauge';
import AuthorizationBudgetStrip from './AuthorizationBudgetStrip';
import type { EpisodeLupaPeriodResponse } from '../services/api';
import type { AuthorizationBudget } from '../services/api/types.gen';
import EpisodeCalendarLegend from './EpisodeCalendarLegend';
import {
  groupVisitsByDate,
  parseEpisodeDateRange,
  useEpisodeCalendarMarkers,
  visitDayKinds,
} from './useEpisodeCalendarMarkers';

interface VisitsCalendarPanelProps {
  visits: TaskListResponse[];
  onVisitClick: (visit: TaskListResponse) => void;
  onCreateTaskFromDate: (date: Date) => void;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Day Roger charts this episode from; calendar markers when it straddles. */
  rogerChartsFrom?: string | null;
  clinicians: CalendarCreateVisitClinician[];
  /** Set of display names that appear for more than one distinct user. */
  duplicateClinicianNames?: Set<string>;
  /** Per-30-day-period LUPA thresholds; omit when not applicable. */
  lupaPeriods?: EpisodeLupaPeriodResponse[];
  /**
   * The episode's unfiltered tasks behind the LUPA counts' visit popovers.
   * `visits` follows the table's task-type filter, but the counts never do, so
   * listing from it would show a subset of what the count says. Defaults to
   * `visits`.
   */
  lupaVisits?: TaskListResponse[];
  /** True while `lupaPeriods` is being fetched, so the strip holds its space. */
  lupaLoading?: boolean;
  /** Server-computed per-authorization used/allowed rows; omit when not applicable. */
  authorizationBudget?: AuthorizationBudget[];
  /** True while `authorizationBudget` is being fetched. */
  authorizationBudgetLoading?: boolean;
  /** True when the episode's insurance requires an authorization on file. */
  requiresAuthorization?: boolean;
  /** How many visits spend an authorized visit but aren't linked to one. */
  unlinkedVisitCount?: number;
  /**
   * The authorization request failed, so used/allowed is unknown. Rendered as
   * an explicit "unavailable" note -- otherwise a failed fetch is
   * indistinguishable from "this episode needs no authorization".
   */
  authorizationBudgetUnavailable?: boolean;
}

/**
 * Placeholder rows for a strip still being fetched, in the same shape the real
 * one lands in: a short label line and a full-width bar per period (two count
 * lines + two bars). Without it the column has no width until the data arrives
 * and the calendar jumps sideways when it does.
 */
const StripSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <div className='space-y-3' data-testid='strip-skeleton'>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className='space-y-1.5'>
        <div className='mb-1 flex items-center justify-between gap-2'>
          <Skeleton className='h-3 w-20' />
          <Skeleton className='h-3 w-12' />
        </div>
        <Skeleton className='h-1.5 w-full rounded-full' />
        <div className='flex items-center justify-end'>
          <Skeleton className='h-3 w-16' />
        </div>
        <Skeleton className='h-1 w-full rounded-full' />
      </div>
    ))}
  </div>
);

const getStatusVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'default';
    case 'scheduled':
      return 'secondary';
    case 'in_progress':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
};

const VisitsCalendarPanel: React.FC<VisitsCalendarPanelProps> = ({
  visits,
  onVisitClick,
  onCreateTaskFromDate,
  clinicians,
  episodeStartDate,
  episodeEndDate,
  rogerChartsFrom,
  lupaPeriods,
  lupaVisits,
  lupaLoading,
  authorizationBudget,
  authorizationBudgetLoading,
  requiresAuthorization,
  unlinkedVisitCount,
  authorizationBudgetUnavailable,
}) => {
  // Three months need ~730px of calendar, which the content area no longer has
  // much below `lg` once the nav sidebar and page/card padding are taken out —
  // and one month rendered whole beats three with the last hidden behind a
  // horizontal scrollbar. No CSS equivalent: `numberOfMonths` is a prop.
  const showThreeMonths = useMediaQuery('(min-width: 1024px)');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [popoutVisible, setPopoutVisible] = useState(false);
  const [popoutPosition, setPopoutPosition] = useState({ x: 0, y: 0 });
  const [popoutDate, setPopoutDate] = useState<Date | undefined>();
  const calendarRef = useRef<HTMLDivElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);

  const episodeDateRange = useMemo(
    () => parseEpisodeDateRange(episodeStartDate, episodeEndDate),
    [episodeStartDate, episodeEndDate]
  );

  // A block in flight holds its own space: the two strips give the column its
  // width, so appearing late shifts the calendar sideways.
  const showLupaThresholds = (lupaPeriods?.length ?? 0) > 0 || !!lupaLoading;

  // Mirrors the LUPA strip's own gate (only render when there's real content)
  // rather than always showing an empty "Authorization budget" section.
  // Deliberately not gated on `authorizationBudgetLoading`. Doing so rendered
  // the heading and skeleton for every episode on first load — self-pay ones
  // included — and then tore the whole block back out when the fetch resolved
  // `requires_authorization: false`, reflowing the LUPA strip beside it
  // (`justify-around`). Waiting until the episode is known to need
  // authorization never promises a budget that isn't coming; PatientDetail's
  // own on-screen ref keeps the skeleton for re-fetches, where the block has
  // already earned its place.
  const showAuthorizationBudget =
    (authorizationBudget?.length ?? 0) > 0 ||
    (requiresAuthorization ?? false) ||
    (authorizationBudgetUnavailable ?? false);

  const defaultMonth = useMemo(() => {
    if (episodeDateRange) {
      return new Date(
        episodeDateRange.start.getFullYear(),
        episodeDateRange.start.getMonth(),
        1
      );
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, [episodeDateRange]);

  const dateRange = useMemo(() => {
    const from = defaultMonth;
    const toDate = new Date(from.getFullYear(), from.getMonth() + 3, 0);
    return { fromDate: from, toDate };
  }, [defaultMonth]);

  const visitsByDate = useMemo(
    () => groupVisitsByDate(visits, dateRange.fromDate, dateRange.toDate),
    [visits, dateRange]
  );

  const visitDays = useMemo(() => visitDayKinds(visitsByDate), [visitsByDate]);

  const {
    hasTwoPdgmPeriods,
    showChartsFromCutoff,
    classNames: markerClassNames,
    modifiers: markerModifiers,
    modifiersClassNames: markerModifiersClassNames,
    modifiersStyles: markerModifiersStyles,
  } = useEpisodeCalendarMarkers({
    episodeStartDate,
    episodeEndDate,
    rogerChartsFrom,
    lupaPeriods,
    visitDays,
  });

  const handleDateClick = (date: Date, event?: React.MouseEvent) => {
    if (
      episodeDateRange &&
      (date < episodeDateRange.start || date > episodeDateRange.end)
    ) {
      return;
    }

    setSelectedDate(date);
    const dateKey = date.toDateString();
    const dayVisits = visitsByDate.get(dateKey);

    if (!dayVisits || dayVisits.length === 0) {
      setPopoutVisible(false);
      onCreateTaskFromDate(date);
    } else {
      if (event) {
        setPopoutPosition({
          x: event.clientX,
          y: event.clientY + 10,
        });
      }
      setPopoutDate(date);
      setPopoutVisible(true);
    }
  };

  useCalendarVisitPopout({
    popoutVisible,
    popoutRef,
    popoutPosition,
    setPopoutPosition,
    popoutDate,
    visitsByDate,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        calendarRef.current?.contains(target) ||
        popoutRef.current?.contains(target)
      ) {
        return;
      }

      setPopoutVisible(false);
    };
    if (popoutVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popoutVisible]);

  const renderVisitPopout = () => {
    if (!popoutVisible || !popoutDate) return null;
    const dateKey = popoutDate.toDateString();
    const dayVisits = visitsByDate.get(dateKey);
    if (!dayVisits || dayVisits.length === 0) return null;

    const popout = (
      <div
        ref={popoutRef}
        data-testid='calendar-visit-popout'
        className='fixed z-[1000] bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[min(16rem,calc(100vw-2rem))] max-w-[min(20rem,calc(100vw-2rem))]'
        style={{
          left: `${popoutPosition.x}px`,
          top: `${popoutPosition.y}px`,
          transform: 'translateX(-50%)',
        }}
      >
        <div className='flex items-center justify-between mb-2'>
          <div className='font-medium text-xs text-gray-700'>
            {popoutDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setPopoutVisible(false)}
            className='h-5 w-5 p-0'
          >
            <X className='h-3 w-3' />
          </Button>
        </div>
        <div className='space-y-1.5 max-h-48 overflow-y-auto'>
          {dayVisits.map(visit => (
            <div
              key={visit.id}
              className='flex items-center justify-between p-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors'
              onClick={() => {
                onVisitClick(visit);
                setPopoutVisible(false);
              }}
            >
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-1.5 mb-0.5'>
                  <Badge
                    variant={getStatusVariant(visit.status)}
                    className='text-[10px] px-1 py-0'
                  >
                    {visit.display_name}
                  </Badge>
                  <Badge variant='outline' className='text-[10px] px-1 py-0'>
                    {visit.status}
                  </Badge>
                  {visit.missed_visit_data && (
                    <Badge
                      variant='destructive'
                      className='text-[10px] px-1 py-0'
                    >
                      Missed
                    </Badge>
                  )}
                </div>
                <div className='text-[10px] text-gray-700'>
                  {visit.assigned_clinician_id
                    ? (() => {
                        const clinician = clinicians.find(
                          c => c.id === visit.assigned_clinician_id
                        );
                        return clinician
                          ? getClinicianDisplayName(clinician)
                          : 'Clinician not found';
                      })()
                    : 'Not assigned'}
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button
          size='sm'
          className='mt-3 w-full'
          onClick={() => {
            onCreateTaskFromDate(popoutDate);
            setPopoutVisible(false);
          }}
        >
          Add Task
        </Button>
      </div>
    );

    return createPortal(popout, document.body);
  };

  return (
    // Calendar left, LUPA + authorization bars in a column to its right.
    // The side-by-side row is gated at `2xl`, not `md`: three
    // months need ~730px and the column takes another 224px, so anything
    // narrower squeezes the calendar into a horizontally-scrolling sliver.
    // Below `xl` the column falls back under the calendar, which keeps the
    // full row width it had before this change.
    //
    // `xl` (1280px) is the viewport, not the space this row gets: the nav
    // sidebar and the page/card padding take ~300px, leaving ~980px. A 320px
    // column needed ~1050px and hid the third month behind the wrapper's
    // `overflow-x-auto`, so the row was gated at `2xl` — which no laptop
    // reaches (a 14" MBP is 1512px). At `w-56` the pair needs ~954px and
    // fits, so the strips sit beside the calendar on the screens schedulers
    // actually use.
    <div className='flex flex-col gap-2 lg:gap-4 xl:flex-row xl:items-stretch xl:justify-around xl:gap-0'>
      <div className='min-w-0'>
        <div
          className='relative flex justify-center overflow-x-auto'
          ref={calendarRef}
        >
          {/* Calendar rendered at natural w-fit size, centered, not stretched */}
          <Calendar
            mode='single'
            selected={selectedDate}
            defaultMonth={defaultMonth}
            onSelect={date => {
              if (date) handleDateClick(date);
            }}
            onDayClick={(date, _modifiers, event) => {
              handleDateClick(date, event);
            }}
            disabled={[
              { before: dateRange.fromDate },
              { after: dateRange.toDate },
            ]}
            numberOfMonths={showThreeMonths ? 3 : 1}
            showOutsideDays={false}
            className='max-lg:p-1 max-lg:[--cell-size:1.75rem]'
            classNames={markerClassNames}
            modifiers={markerModifiers}
            modifiersClassNames={markerModifiersClassNames}
            modifiersStyles={markerModifiersStyles}
          />
          {renderVisitPopout()}
        </div>

        {/* Compact legend — hidden on the phone-width episode view so the
            calendar does not push the task list off-screen. Desktop keeps
            the key because the three-month strip has room for it. */}
        <EpisodeCalendarLegend
          hasTwoPdgmPeriods={hasTwoPdgmPeriods}
          showChartsFromCutoff={showChartsFromCutoff}
          className='max-lg:hidden'
        />
      </div>

      {(showLupaThresholds || showAuthorizationBudget) && (
        // The two blocks are spread down the calendar's height rather than
        // stacked at the top, so each gets room to breathe instead of reading
        // as one dense list.
        //
        // `gap-6` is a floor, not a layout: `justify-around` still spreads the
        // blocks when the calendar leaves slack. Desktop used to drop the gap
        // to zero and lean on that spreading alone, which left the two
        // headings touching as soon as the content filled the column — an
        // episode with two authorizations, say. When the blocks no longer fit
        // in the calendar's height the row grows to hold them (the parent is
        // `xl:items-stretch`), rather than the strip compressing.
        <div className='flex w-full shrink-0 flex-col justify-around gap-6 pt-2 xl:w-56'>
          {/* Rendered whenever the episode has periods, including when no
              threshold is determinable yet: the gauge names what is still
              missing, which an empty space cannot do.*/}
          {showLupaThresholds && (
            <div>
              {/* Set in the foreground colour, not grey: these carry the same
                  weight in the row as the calendar's month captions. Both
                  block headings stand 8px off their content — the one rhythm
                  the whole strip is built on.*/}
              <div className='mb-2 select-none text-xs font-semibold uppercase tracking-wider text-foreground'>
                LUPA thresholds
              </div>
              {lupaLoading ? (
                <StripSkeleton rows={2} />
              ) : (
                <LupaThresholdGauge
                  periods={lupaPeriods ?? []}
                  visits={lupaVisits ?? visits}
                />
              )}
            </div>
          )}

          {showAuthorizationBudget && (
            <div>
              <div className='mb-2 select-none text-xs font-semibold uppercase tracking-wider text-foreground'>
                Auth budget
              </div>
              {authorizationBudgetLoading ? (
                <StripSkeleton rows={2} />
              ) : (
                <AuthorizationBudgetStrip
                  budget={authorizationBudget ?? []}
                  requiresAuthorization={requiresAuthorization ?? false}
                  unlinkedVisitCount={unlinkedVisitCount ?? 0}
                  unavailable={authorizationBudgetUnavailable ?? false}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitsCalendarPanel;
